/**
 * Logistics Management System
 * Handles guide assignments, vehicle allocation, accommodation, and trip logistics
 */

// Cache invalidation trigger
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  UserGroupIcon,
  TruckIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  ClockIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  UsersIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';
import { transportService } from '../../services/transportService';
import { accommodationPoolService } from '../../services/accommodationPoolService';
import { TransportOption, LogisticsAccommodationPool } from '../../types/logistics';
import { useToast } from '../../contexts/ToastContext';
import { TrashIcon } from '@heroicons/react/24/solid';

interface Guide {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string[];
  languages: string[];
  experience: number; // years
  rating: number;
  availability: boolean;
  currentBookings: number;
  maxBookings: number;
}

interface Vehicle {
  id: string;
  registration: string;
  type: string;
  capacity: number;
  features: string[];
  driver: string;
  driverPhone: string;
  availability: boolean;
  currentBookings: number;
  maxBookings: number;
  lastService: string;
  nextService: string;
}

interface Accommodation {
  id: string;
  name: string;
  type: 'lodge' | 'camp' | 'hotel' | 'tent';
  location: string;
  capacity: number;
  features: string[];
  contact: string;
  phone: string;
  email: string;
  availability: boolean;
  currentBookings: number;
  maxBookings: number;
}

interface BookingLogistics {
  bookingId: string;
  tourName: string;
  tourDate: string;
  participants: number;
  guideId?: string;
  vehicleId?: string;
  accommodationId?: string;
  pickupLocation: string;
  pickupTime: string;
  dropoffLocation: string;
  dropoffTime: string;
  specialRequirements: string[];
  notes: string;
  status: 'pending' | 'assigned' | 'confirmed' | 'in_progress' | 'completed';
}

interface LogisticsManagementProps {
  onLogisticsUpdate?: (logistics: BookingLogistics) => void;
}

export const LogisticsManagement: React.FC<LogisticsManagementProps> = ({ onLogisticsUpdate }) => {
  const { actualTheme } = useTheme();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [bookings, setBookings] = useState<BookingLogistics[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<BookingLogistics | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'fleet' | 'accommodations'>('overview');
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading logistics data (Sequential Mode)...');

      // 1. Fetch Logistics Summary
      try {
        const logisticsSummary = await apiService.getLogisticsSummary();
        setSummary(logisticsSummary);
      } catch (e) {
        console.error('❌ Failed to fetch logistics summary:', e);
      }

      // 2. Fetch Guides
      try {
        const guidesData = await apiService.getAllGuides();
        setGuides(guidesData || []);
      } catch (e) {
        console.error('❌ Failed to fetch guides:', e);
      }

      // 3. Fetch Vehicles
      try {
        const vehiclesData = await transportService.getTransportOptions();
        setVehicles((vehiclesData || []).map((v: any) => ({
          id: v.id,
          registration: v.registration,
          type: v.type,
          capacity: v.capacity,
          features: v.features || [],
          driver: v.driver || 'No Driver Assigned',
          driverPhone: v.driverPhone || '',
          availability: v.active !== false,
          currentBookings: 0,
          maxBookings: 1,
          lastService: v.lastService || '',
          nextService: v.nextService || ''
        })));
      } catch (e) {
        console.error('❌ Failed to fetch vehicles:', e);
      }

      // 4. Fetch Accommodations
      try {
        const accommodationsData = await accommodationPoolService.getAccommodationPools();
        setAccommodations((accommodationsData || []).map((a: any) => ({
          id: a.id,
          name: a.name,
          type: a.tier || 'lodge',
          location: a.locationId || 'Unknown',
          capacity: 10,
          features: [],
          contact: 'Manager',
          phone: '',
          email: '',
          availability: true,
          currentBookings: 0,
          maxBookings: 10
        })));
      } catch (e) {
        console.error('❌ Failed to fetch accommodations:', e);
      }

      // 5. Fetch Bookings/Aggregation
      try {
        const bookingsData = await apiService.getLogisticsAggregation();
        setBookings((bookingsData || []).map((b: any) => ({
          bookingId: b.id,
          tourName: b.tourName || 'Safari Trip',
          tourDate: b.startDate || '',
          participants: b.total_visitors || 0,
          guideId: b.guideId,
          vehicleId: b.vehicleId,
          accommodationId: b.accommodationId,
          status: b.status === 'preparing' ? 'pending' : 'assigned',
          pickupLocation: '',
          pickupTime: '',
          dropoffLocation: '',
          dropoffTime: '',
          specialRequirements: [],
          notes: ''
        })));
      } catch (e) {
        console.error('❌ Failed to fetch bookings aggregation:', e);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading logistics data:', err);
      showError('Error', 'Failed to load live logistics data.');
      setLoading(false);
    }
  };

  const handleAssignment = (bookingId: string, type: 'guide' | 'vehicle' | 'accommodation', id: string) => {
    setBookings(prev => prev.map(booking =>
      booking.bookingId === bookingId
        ? { ...booking, [`${type}Id`]: id, status: 'assigned' as any }
        : booking
    ));
  };

  const handleLogisticsUpdate = (bookingId: string, updates: Partial<BookingLogistics>) => {
    setBookings(prev => prev.map(booking =>
      booking.bookingId === bookingId
        ? { ...booking, ...updates }
        : booking
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'assigned': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'confirmed': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'in_progress': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const renderAssignmentModal = () => {
    if (!selectedBooking) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className={`rounded-2xl shadow-modern backdrop-blur-sm border max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
          : 'bg-white/90 border-neutral-200/50'
          }`}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Assign Logistics - {selectedBooking.bookingId}</h2>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className={`transition-colors duration-300 ${actualTheme === 'dark'
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Guide Assignment */}
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Assign Guide</h3>
                <div className="space-y-3">
                  {guides.filter(guide => guide.availability).map(guide => (
                    <div
                      key={guide.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-300 hover-magnify-sm ${selectedBooking.guideId === guide.id
                        ? actualTheme === 'dark'
                          ? 'border-blue-500 bg-blue-900/20'
                          : 'border-blue-500 bg-blue-50'
                        : actualTheme === 'dark'
                          ? 'border-slate-600 hover:border-slate-500'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => handleAssignment(selectedBooking.bookingId, 'guide', guide.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{guide.name}</div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>
                            {guide.specialization.join(', ')}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            {guide.experience} years • Rating: {guide.rating}/5
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            {guide.currentBookings}/{guide.maxBookings} bookings
                          </div>
                          <div className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
                            }`}>
                            {guide.languages.join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vehicle Assignment */}
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Assign Vehicle</h3>
                <div className="space-y-3">
                  {vehicles.filter(vehicle => vehicle.availability).map(vehicle => (
                    <div
                      key={vehicle.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-300 hover-magnify-sm ${selectedBooking.vehicleId === vehicle.id
                        ? actualTheme === 'dark'
                          ? 'border-blue-500 bg-blue-900/20'
                          : 'border-blue-500 bg-blue-50'
                        : actualTheme === 'dark'
                          ? 'border-slate-600 hover:border-slate-500'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => handleAssignment(selectedBooking.bookingId, 'vehicle', vehicle.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{vehicle.registration}</div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>
                            {vehicle.type} • Capacity: {vehicle.capacity}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            Driver: {vehicle.driver}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            {vehicle.currentBookings}/{vehicle.maxBookings} bookings
                          </div>
                          <div className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
                            }`}>
                            Next service: {vehicle.nextService}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accommodation Assignment */}
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Assign Accommodation</h3>
                <div className="space-y-3">
                  {accommodations.filter(accommodation => accommodation.availability).map(accommodation => (
                    <div
                      key={accommodation.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-300 hover-magnify-sm ${selectedBooking.accommodationId === accommodation.id
                        ? actualTheme === 'dark'
                          ? 'border-blue-500 bg-blue-900/20'
                          : 'border-blue-500 bg-blue-50'
                        : actualTheme === 'dark'
                          ? 'border-slate-600 hover:border-slate-500'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => handleAssignment(selectedBooking.bookingId, 'accommodation', accommodation.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className={`font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>{accommodation.name}</div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                            }`}>
                            {accommodation.type} • {accommodation.location}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            Capacity: {accommodation.capacity}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                            }`}>
                            {accommodation.currentBookings}/{accommodation.maxBookings} bookings
                          </div>
                          <div className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
                            }`}>
                            {accommodation.phone}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Logistics Details */}
            <div className="mt-6 space-y-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Logistics Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                    }`}>Pickup Location</label>
                  <input
                    type="text"
                    value={selectedBooking.pickupLocation}
                    onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { pickupLocation: e.target.value })}
                    className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                    }`}>Pickup Time</label>
                  <input
                    type="time"
                    value={selectedBooking.pickupTime}
                    onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { pickupTime: e.target.value })}
                    className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                    }`}>Dropoff Location</label>
                  <input
                    type="text"
                    value={selectedBooking.dropoffLocation}
                    onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { dropoffLocation: e.target.value })}
                    className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                    }`}>Dropoff Time</label>
                  <input
                    type="time"
                    value={selectedBooking.dropoffTime}
                    onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { dropoffTime: e.target.value })}
                    className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>Special Requirements</label>
                <textarea
                  value={selectedBooking.specialRequirements.join(', ')}
                  onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { specialRequirements: e.target.value.split(', ') })}
                  className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                    }`}
                  rows={3}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>Notes</label>
                <textarea
                  value={selectedBooking.notes}
                  onChange={(e) => handleLogisticsUpdate(selectedBooking.bookingId, { notes: e.target.value })}
                  className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                    }`}
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowAssignmentModal(false)}
                className={`px-4 py-2 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                  ? 'bg-slate-600 hover:bg-slate-500 text-slate-200'
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogisticsUpdate(selectedBooking.bookingId, { status: 'confirmed' });
                  setShowAssignmentModal(false);
                }}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus"
              >
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-t-2xl overflow-hidden">
        {[
          { id: 'overview', label: 'Operations Overview', icon: <MapPinIcon className="w-4 h-4" /> },
          { id: 'fleet', label: 'Fleet & Transport', icon: <TruckIcon className="w-4 h-4" /> },
          { id: 'accommodations', label: 'Accommodation Pools', icon: <BuildingOfficeIcon className="w-4 h-4" /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all border-b-4 ${activeTab === tab.id
              ? 'border-primary-600 text-primary-600 bg-white dark:bg-slate-800'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' ? (
        <>
      {/* Header */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
        : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className="p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className={`text-3xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Logistics Management</h1>
              <p className={`mt-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                }`}>Manage guides, vehicles, and accommodations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Logistics Summary Metrics */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`p-6 rounded-2xl shadow-modern border transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-800/40 border-slate-600/30' : 'bg-white border-neutral-200/50'
            }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <UsersIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Visitors</p>
                <h3 className="text-2xl font-bold dark:text-white">{summary.total_visitors || 0}</h3>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-2xl shadow-modern border transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-800/40 border-slate-600/30' : 'bg-white border-neutral-200/50'
            }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                <TicketIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Active Groups</p>
                <h3 className="text-2xl font-bold dark:text-white">{summary.active_groups || 0}</h3>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-2xl shadow-modern border transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-800/40 border-slate-600/30' : 'bg-white border-neutral-200/50'
            }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                <CurrencyDollarIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Revenue Collected</p>
                <h3 className="text-2xl font-bold dark:text-white">${summary.total_collected?.toLocaleString() || 0}</h3>
              </div>
            </div>
          </div>

          <div className={`p-6 rounded-2xl shadow-modern border transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-800/40 border-slate-600/30' : 'bg-white border-neutral-200/50'
            }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                <ClockIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Expected</p>
                <h3 className="text-2xl font-bold dark:text-white">${summary.total_expected?.toLocaleString() || 0}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookings List */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
        : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-gray-200'
          }`}>
          <h2 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>Booking Assignments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y transition-colors duration-300" style={{
            '--tw-divide-opacity': actualTheme === 'dark' ? '0.2' : '1'
          } as React.CSSProperties}>
            <thead className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
              }`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Booking
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Tour
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Guide
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Vehicle
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Accommodation
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-500'
                  }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-slate-800/50 divide-slate-600/30' : 'bg-white divide-gray-200'
              }`}>
              {bookings.map((booking) => {
                const guide = guides.find(g => g.id === booking.guideId);
                const vehicle = vehicles.find(v => v.id === booking.vehicleId);
                const accommodation = accommodations.find(a => a.id === booking.accommodationId);

                return (
                  <tr key={booking.bookingId} className={`transition-colors duration-300 ${actualTheme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                    }`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>
                          {booking.bookingId}
                        </div>
                        <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                          {new Date(booking.tourDate).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                          }`}>
                          {booking.tourName}
                        </div>
                        <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                          {booking.participants} {booking.participants === 1 ? 'Person' : 'People'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {guide ? (
                        <div>
                          <div className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {guide.name}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>
                            {guide.phone}
                          </div>
                        </div>
                      ) : (
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle ? (
                        <div>
                          <div className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {vehicle.registration}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>
                            {vehicle.type}
                          </div>
                        </div>
                      ) : (
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {accommodation ? (
                        <div>
                          <div className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>
                            {accommodation.name}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>
                            {accommodation.location}
                          </div>
                        </div>
                      ) : (
                        <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                          }`}>Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {(booking.status || 'pending').replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowAssignmentModal(true);
                        }}
                        className={`transition-colors duration-300 ${actualTheme === 'dark'
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-900'
                          }`}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assignment Modal */}
        </>
      ) : activeTab === 'fleet' ? (
        <FleetManager />
      ) : (
        <AccommodationPoolManager />
      )}
    </div>
  );
};

const FleetManager: React.FC = () => {
    const { actualTheme } = useTheme();
    const { showSuccess, showError } = useToast();
    const [options, setOptions] = useState<TransportOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [newOption, setNewOption] = useState({ name: '', capacity: 7, basePricePerDay: 0, description: '' });

    const loadOptions = async () => {
        setLoading(true);
        try {
            const data = await transportService.getTransportOptions();
            setOptions(data);
        } catch {
            showError('Failed to load transport options', 'Error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadOptions(); }, []);

    const handleSave = async () => {
        try {
            await transportService.createTransportOption({ ...newOption, type: '4x4_safari_landcruiser', availability: true } as any);
            showSuccess('Transport option added', 'Success');
            setIsAdding(false);
            setNewOption({ name: '', capacity: 7, basePricePerDay: 0, description: '' });
            loadOptions();
        } catch {
            showError('Failed to add transport option', 'Error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this transport option?')) return;
        try {
            await transportService.deleteTransportOption(id);
            showSuccess('Option deleted', 'Success');
            loadOptions();
        } catch {
            showError('Failed to delete option', 'Error');
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold dark:text-white">Fleet & Transport Management</h3>
                    <p className="text-sm text-gray-500">Manage safari vehicles and transport pricing variants</p>
                </div>
                <button
                    onClick={() => setIsAdding(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-primary-700 transition-all"
                >
                    + Add Vehicle Variant
                </button>
            </div>

            {isAdding && (
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border-2 border-primary-500/20 shadow-xl space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Vehicle Name</label>
                            <input
                                value={newOption.name}
                                onChange={e => setNewOption({ ...newOption, name: e.target.value })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                                placeholder="e.g. Extended 4x4 Land Cruiser"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Capacity (Pax)</label>
                            <input
                                type="number"
                                value={newOption.capacity}
                                onChange={e => setNewOption({ ...newOption, capacity: Number(e.target.value) })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Base Price Adjustment ($)</label>
                            <input
                                type="number"
                                value={newOption.basePricePerDay}
                                onChange={e => setNewOption({ ...newOption, basePricePerDay: Number(e.target.value) })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-500 font-bold">Cancel</button>
                        <button onClick={handleSave} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold">Save Vehicle</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full py-20 text-center animate-pulse text-gray-400">Loading fleet data...</div>
                ) : options.map(opt => (
                    <div key={opt.id} className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm relative group overflow-hidden">
                        <div className="flex justify-between items-start">
                            <div>
                                <h4 className="font-bold text-lg dark:text-white">{opt.name}</h4>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold mt-1">
                                    <UsersIcon className="h-3 w-3" /> {opt.capacity} Seats
                                </span>
                            </div>
                            <button onClick={() => handleDelete(opt.id)} className="p-2 text-red-200 hover:text-red-500 transition-colors">
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-50 dark:border-slate-700 flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-400">Base Price Impact</span>
                            <span className={`text-lg font-black ${opt.basePricePerDay >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>
                                {opt.basePricePerDay >= 0 ? '+' : ''}${opt.basePricePerDay}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AccommodationPoolManager: React.FC = () => {
    const { actualTheme } = useTheme();
    const { showSuccess, showError } = useToast();
    const [pools, setPools] = useState<LogisticsAccommodationPool[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [newPool, setNewPool] = useState({ name: '', locationId: '', tier: 'luxury' as any, pricePerNight: 0 });

    const loadPools = async () => {
        setLoading(true);
        try {
            const data = await accommodationPoolService.getAccommodationPools();
            setPools(data);
        } catch {
            showError('Failed to load accommodation pools', 'Error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPools(); }, []);

    const handleSave = async () => {
        try {
            await accommodationPoolService.createAccommodationPool({ 
                name: newPool.name,
                description: '',
                locationId: newPool.locationId,
                providers: [],
                tier: newPool.tier as any,
                pricePerNight: newPool.pricePerNight
            });
            showSuccess('Pool added successfully', 'Success');
            setIsAdding(false);
            setNewPool({ name: '', locationId: '', tier: 'luxury', pricePerNight: 0 });
            loadPools();
        } catch {
            showError('Failed to add pool', 'Error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this accommodation pool?')) return;
        try {
            await accommodationPoolService.deleteAccommodationPool(id);
            showSuccess('Pool deleted', 'Success');
            loadPools();
        } catch {
            showError('Failed to delete pool', 'Error');
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold dark:text-white">Accommodation & Hotel Pools</h3>
                    <p className="text-sm text-gray-500">Manage standard/luxury accommodation tiers and pricing</p>
                </div>
                <button
                    onClick={() => setIsAdding(true)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-emerald-700 transition-all"
                >
                    + Add New Pool
                </button>
            </div>

            {isAdding && (
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border-2 border-emerald-500/20 shadow-xl space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Pool Name</label>
                            <input
                                value={newPool.name}
                                onChange={e => setNewPool({ ...newPool, name: e.target.value })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                                placeholder="e.g. Standard Safari Lodges"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Tier</label>
                            <select
                                value={newPool.tier}
                                onChange={e => setNewPool({ ...newPool, tier: e.target.value as any })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                            >
                                <option value="budget">Budget / Camping</option>
                                <option value="mid_range">Mid-Range / Comfort</option>
                                <option value="luxury">Luxury / Premium</option>
                                <option value="ultra_luxury">Ultra-Luxury / Signature</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Price/Night ($)</label>
                            <input
                                type="number"
                                value={newPool.pricePerNight}
                                onChange={e => setNewPool({ ...newPool, pricePerNight: Number(e.target.value) })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Location Focus (ID)</label>
                            <input
                                value={newPool.locationId}
                                onChange={e => setNewPool({ ...newPool, locationId: e.target.value })}
                                className="w-full px-4 py-2 border dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-xl"
                                placeholder="e.g. MASAI_MARA"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-500 font-bold">Cancel</button>
                        <button onClick={handleSave} className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-bold">Create Pool</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pools.map(pool => (
                    <div key={pool.id} className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm flex gap-4 group">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl ${
                            pool.tier.includes('luxury') ? 'bg-amber-100 text-amber-600' : 'bg-primary-100 text-primary-600'
                        }`}>
                            {pool.tier.includes('luxury') ? '💎' : '🏕️'}
                        </div>
                        <div className="flex-1">
                            <div className="flex justify-between">
                                <h4 className="font-bold text-lg dark:text-white">{pool.name}</h4>
                                <button onClick={() => handleDelete(pool.id)} className="text-red-200 hover:text-red-500">
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-500">{pool.locationId} · <span className="capitalize font-medium">{pool.tier.replace('_', ' ')}</span></p>
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-400 uppercase">Avg Rate/Night</span>
                                <span className="text-xl font-black text-emerald-600">${pool.pricePerNight}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
